/**
 * Движок дивергенций SPECTER.
 *
 * Расхождение — это когда цена и поток сделок говорят разное: цена
 * ставит новый минимум, а продавать стали меньше. Модуль отвечает не
 * «расхождение есть», а на восемь вопросов сразу: какое, куда, между чем
 * и чем, когда стало известно, насколько выражено, чем подтверждено, живо
 * ли ещё и что это значит словами.
 *
 * Шесть решений, без которых такой расчёт превращается в самообман.
 *
 * **Вершины, а не бары.** Сравниваются локальные экстремумы (`swings`) —
 * те же, что видит вся остальная структура платформы. Сравнение «цена
 * сейчас и цена четыре часа назад» ловит рябь, а не расхождение.
 *
 * **Ничего задним числом.** Экстремум подтверждается только через `right`
 * баров после себя, и расхождение появляется на баре подтверждения —
 * там, где его впервые мог увидеть человек. Время события и время
 * подтверждения хранятся порознь, чтобы проверку на истории нельзя было
 * незаметно улучшить.
 *
 * **Не доллары, а размахи.** Цена меряется в ATR, поток — в стандартных
 * отклонениях своих изменений. «CVD упал на двадцать миллионов» для BTC и
 * для мелкой монеты — разные события, и один порог на всех означал бы
 * тысячи ложных срабатываний на одних и молчание на других.
 *
 * **Поправка на длину.** Между вершинами бывает десять баров, а бывает
 * сто, и накопленный поток за сто баров естественно больше. Порог растёт
 * как корень из числа баров — так же, как растёт размах случайного
 * блуждания, — иначе далёкие вершины всегда выглядели бы «сильным»
 * расхождением.
 *
 * **Три класса врозь.** Цена против спота, цена против контрактов и спот
 * против контрактов — разные утверждения о рынке. Последнее — вообще не
 * классическая дивергенция, и называется оно здесь «расхождением
 * потоков».
 *
 * **Счёт — качество расхождения, а не вероятность разворота.** Ни одно
 * число здесь не обещает движения цены; оно говорит лишь о том,
 * насколько выражено и подтверждено само расхождение.
 */
import { atr, rollingStdDev } from './math.js';
import { swings } from './structure.js';
export const DIVERGENCE_DEFAULTS = {
    left: 5,
    right: 5,
    maxGap: 60,
    priceThreshold: 0.5,
    valueThreshold: 0.5,
    atrPeriod: 14,
    deviationPeriod: 50,
    minScore: 30,
    hidden: true,
    weights: {
        price: 20,
        value: 20,
        pivot: 15,
        volume: 10,
        openInterest: 10,
        second: 10,
        higher: 10,
        span: 5,
    },
};
/* ------------------------------------------------------------- расчёт */
/**
 * Все расхождения на ряде — подтверждённые, отменённые и формирующееся.
 *
 * Порядок — по времени, когда о расхождении стало известно. Отменённые
 * не выбрасываются: человек должен видеть, что сигнал был и чем
 * кончился, иначе история выглядит чище, чем была.
 */
export function divergenceScan(input, settings = {}) {
    const s = {
        ...DIVERGENCE_DEFAULTS,
        ...settings,
        weights: { ...DIVERGENCE_DEFAULTS.weights, ...(settings.weights ?? {}) },
    };
    const bars = input.bars;
    if (bars.length < s.left + s.right + 2)
        return [];
    const highs = bars.map((bar) => bar.high);
    const lows = bars.map((bar) => bar.low);
    const closes = bars.map((bar) => bar.close);
    const range = atr(highs, lows, closes, s.atrPeriod);
    const pivots = swings(bars, s.left, s.right);
    const found = [];
    const sources = [];
    if (input.spot)
        sources.push({ source: 'spot', series: input.spot });
    if (input.futures)
        sources.push({ source: 'futures', series: input.futures });
    const scales = new Map();
    for (const entry of sources)
        scales.set(entry.source, changeDeviation(entry.series, s.deviationPeriod));
    const volumeAverage = average(bars.map((bar) => bar.volume), s.deviationPeriod);
    for (const entry of sources) {
        const lowsSeen = [];
        const highsSeen = [];
        for (let i = 0; i < bars.length; i += 1) {
            const fresh = freshPivot(pivots, i);
            if (!fresh)
                continue;
            const pivot = pivotAt(bars, entry.series, fresh.at, fresh.kind);
            if (!pivot)
                continue;
            const list = fresh.kind === 'low' ? lowsSeen : highsSeen;
            const previous = list[list.length - 1] ?? null;
            list.push(pivot);
            if (list.length > 3)
                list.shift();
            if (!previous || pivot.index - previous.index > s.maxGap)
                continue;
            const made = pairOf({
                bars,
                i,
                source: entry.source,
                kind: fresh.kind,
                from: previous,
                to: pivot,
                atrAt: range[pivot.index] ?? null,
                scale: scales.get(entry.source)?.[pivot.index] ?? null,
                volumeAverage: volumeAverage[pivot.index] ?? null,
                input,
                settings: s,
                other: entry.source === 'spot' ? input.futures : input.spot,
            });
            if (made)
                found.push(made);
            // Расхождение потоков живёт на тех же вершинах цены, но сравнивает
            // спот с контрактами: спорят ли между собой два потока.
            if (entry.source === 'spot' && input.futures) {
                const flow = flowAt({
                    bars,
                    i,
                    from: previous,
                    to: pivot,
                    spot: input.spot,
                    futures: input.futures,
                    spotScale: scales.get('spot')?.[pivot.index] ?? null,
                    futuresScale: scales.get('futures')?.[pivot.index] ?? null,
                    atrAt: range[pivot.index] ?? null,
                    volumeAverage: volumeAverage[pivot.index] ?? null,
                    input,
                    settings: s,
                });
                if (flow)
                    found.push(flow);
            }
        }
    }
    const withState = found
        .map((divergence) => withInvalidation(divergence, bars))
        .filter((divergence) => divergence.score >= s.minScore)
        .filter((divergence) => s.hidden || divergence.kind === 'classic')
        .sort((a, b) => a.signalAt - b.signalAt);
    const forming = formingAt(input, s, range, pivots, scales, volumeAverage);
    return forming ? [...withState, forming] : withState;
}
/** Последняя живая дивергенция каждого источника: то, что показывает табло. */
export function divergenceNow(all) {
    const best = new Map();
    for (const entry of all) {
        if (entry.state === 'invalidated')
            continue;
        const current = best.get(entry.source);
        if (!current || entry.signalAt >= current.signalAt)
            best.set(entry.source, entry);
    }
    return [...best.values()].sort((a, b) => b.score - a.score);
}
/** Пара вершин: расхождение или ничего. */
function pairOf(p) {
    const { from, to, settings: s } = p;
    const span = to.index - from.index;
    if (span <= 0)
        return null;
    const priceGap = Math.abs(to.price - from.price);
    const valueGap = Math.abs(to.value - from.value);
    if (p.atrAt === null || p.atrAt <= 0 || p.scale === null || p.scale <= 0)
        return null;
    const priceZ = priceGap / p.atrAt;
    // Поправка на длину окна: за сто баров поток накапливается сильнее, чем
    // за десять, и без неё далёкие вершины всегда выглядели бы событием.
    const valueZ = valueGap / (p.scale * Math.sqrt(span));
    if (priceZ < s.priceThreshold || valueZ < s.valueThreshold)
        return null;
    const priceUp = to.price > from.price;
    const valueUp = to.value > from.value;
    if (priceUp === valueUp)
        return null;
    const side = p.kind === 'low' ? 'bullish' : 'bearish';
    const classic = p.kind === 'low' ? !priceUp : priceUp;
    return assemble({
        ...p,
        side,
        divergenceKind: classic ? 'classic' : 'hidden',
        span,
        priceZ,
        valueZ,
    });
}
/**
 * Спот против контрактов на одном и том же отрезке.
 *
 * Не классическая дивергенция: цена здесь ни при чём, спорят два потока.
 * Сторона берётся по споту — это покупка самих монет, а не ставка на
 * цену.
 */
function flowAt(f) {
    const s = f.settings;
    const span = f.to.index - f.from.index;
    if (span <= 0 || f.spotScale === null || f.futuresScale === null)
        return null;
    if (f.spotScale <= 0 || f.futuresScale <= 0 || f.atrAt === null || f.atrAt <= 0)
        return null;
    const spotFrom = f.spot[f.from.index];
    const spotTo = f.spot[f.to.index];
    const futuresFrom = f.futures[f.from.index];
    const futuresTo = f.futures[f.to.index];
    if (spotFrom === null ||
        spotFrom === undefined ||
        spotTo === null ||
        spotTo === undefined ||
        futuresFrom === null ||
        futuresFrom === undefined ||
        futuresTo === null ||
        futuresTo === undefined) {
        return null;
    }
    const spotMove = spotTo - spotFrom;
    const futuresMove = futuresTo - futuresFrom;
    if (Math.sign(spotMove) === Math.sign(futuresMove) || spotMove === 0 || futuresMove === 0) {
        return null;
    }
    const spotZ = Math.abs(spotMove) / (f.spotScale * Math.sqrt(span));
    const futuresZ = Math.abs(futuresMove) / (f.futuresScale * Math.sqrt(span));
    if (spotZ < s.valueThreshold || futuresZ < s.valueThreshold)
        return null;
    const side = spotMove > 0 ? 'bullish' : 'bearish';
    return assemble({
        bars: f.bars,
        i: f.i,
        source: 'flow',
        kind: 'low',
        from: { ...f.from, value: spotFrom },
        to: { ...f.to, value: spotTo },
        atrAt: f.atrAt,
        scale: f.spotScale,
        volumeAverage: f.volumeAverage,
        input: f.input,
        settings: s,
        other: f.futures,
        side,
        // Ход цены здесь — контекст, а не условие: расходятся потоки.
        priceZ: Math.abs(f.to.price - f.from.price) / f.atrAt,
        valueZ: Math.min(spotZ, futuresZ),
        span,
        divergenceKind: 'classic',
    });
}
/** Счёт, контекст и слова — общее для всех трёх классов. */
function assemble(a) {
    const s = a.settings;
    const kind = a.divergenceKind;
    const bar = a.bars[a.i];
    const to = a.to;
    const parts = [
        part('price', 'Ход цены между вершинами', ratio(a.priceZ, s.priceThreshold * 3), s.weights.price),
        part('value', 'Расхождение потока', ratio(a.valueZ, s.valueThreshold * 3), s.weights.value),
        part('pivot', 'Чистота вершин', pivotQuality(a.bars, a.from, a.to, s), s.weights.pivot),
    ];
    if (a.volumeAverage !== null && a.volumeAverage > 0) {
        const bars = a.bars;
        const volume = bars[to.index]?.volume ?? 0;
        parts.push(part('volume', 'Объём на вершине', ratio(volume / a.volumeAverage - 1, 1), s.weights.volume));
    }
    const oiChange = changeBetween(a.input.openInterest, a.from.index, to.index);
    if (oiChange !== null) {
        parts.push(part('oi', 'Открытый интерес', ratio(Math.abs(oiChange), 2), s.weights.openInterest));
    }
    const secondMove = changeBetween(a.other, a.from.index, to.index);
    if (secondMove !== null) {
        // Второй поток на той же стороне — это подтверждение; против — нет.
        const supports = a.side === 'bullish' ? secondMove > 0 : secondMove < 0;
        parts.push(part('second', 'Второй поток', supports ? 1 : 0, s.weights.second));
    }
    if (a.input.higher) {
        const agrees = a.input.higher.some((entry) => entry.side === a.side && entry.state !== 'invalidated' && entry.signalAt <= bar.time);
        parts.push(part('higher', 'Старший таймфрейм', agrees ? 1 : 0, s.weights.higher));
    }
    parts.push(part('span', 'Выдержанность', ratio(a.span, s.maxGap * 0.6), s.weights.span));
    const total = parts.reduce((sum, entry) => sum + entry.weight, 0);
    const score = total > 0
        ? Math.round((parts.reduce((sum, entry) => sum + entry.share * entry.weight, 0) / total) * 100)
        : 0;
    const positioning = positioningOf(a.side, kind, oiChange, a.source);
    const words = explain(a.source, kind, a.side, positioning, a.priceZ, a.valueZ, oiChange);
    return {
        id: `${a.source}:${kind}:${a.side}:${a.from.index}-${to.index}`,
        source: a.source,
        kind,
        side: a.side,
        state: a.state ?? 'confirmed',
        signalAt: bar.time,
        pivotAt: to.at,
        invalidatedAt: null,
        from: a.from,
        to,
        score,
        grade: gradeOf(score),
        parts,
        priceZ: round(a.priceZ),
        valueZ: round(a.valueZ),
        positioning,
        openInterestChange: oiChange === null ? null : round(oiChange),
        text: words.text,
        plain: words.plain,
    };
}
/**
 * Расхождение отменяется, когда цена уходит за вторую вершину.
 *
 * Пока она держится, расхождение остаётся утверждением о рынке; как
 * только цена закрылась за ней, утверждение перестало быть верным — и
 * молчать об этом нельзя, иначе на графике вечно висят сигналы, которые
 * рынок давно опроверг.
 */
function withInvalidation(divergence, bars) {
    const start = bars.findIndex((bar) => bar.time > divergence.signalAt);
    if (start < 0)
        return divergence;
    for (let i = start; i < bars.length; i += 1) {
        const bar = bars[i];
        const broken = divergence.side === 'bullish' ? bar.close < divergence.to.price : bar.close > divergence.to.price;
        if (broken) {
            return { ...divergence, state: 'invalidated', invalidatedAt: bar.time };
        }
    }
    return divergence;
}
/**
 * Формирующееся расхождение.
 *
 * Последняя вершина ещё не подтверждена — правое окно не закрыто, — но
 * цена уже сходила за прошлый экстремум, а поток за ней не пошёл. Это не
 * сигнал, и помечено оно отдельно: «формируется». Подтвердится или
 * рассыплется, станет видно через несколько баров.
 */
function formingAt(input, s, range, pivots, scales, volumeAverage) {
    const bars = input.bars;
    const last = bars.length - 1;
    if (last < 1)
        return null;
    for (const source of ['spot', 'futures']) {
        const series = source === 'spot' ? input.spot : input.futures;
        if (!series)
            continue;
        for (const kind of ['low', 'high']) {
            const anchorIndex = (kind === 'low' ? pivots.lowAt[last] : pivots.highAt[last]) ?? null;
            if (anchorIndex === null)
                continue;
            const anchor = pivotAt(bars, series, anchorIndex, kind);
            if (!anchor)
                continue;
            // Крайний бар правого окна: дальше вершины ещё не подтверждались.
            let edge = anchor.index;
            for (let i = anchor.index + 1; i <= last; i += 1) {
                const bar = bars[i];
                const better = kind === 'low' ? bar.low < bars[edge].low : bar.high > bars[edge].high;
                if (better)
                    edge = i;
            }
            if (edge === anchor.index || last - edge >= s.right)
                continue;
            const provisional = pivotAt(bars, series, edge, kind);
            if (!provisional)
                continue;
            const made = pairOf({
                bars,
                i: last,
                source,
                kind,
                from: anchor,
                to: provisional,
                atrAt: range[edge] ?? null,
                scale: scales.get(source)?.[edge] ?? null,
                volumeAverage: volumeAverage[edge] ?? null,
                input,
                settings: s,
                other: source === 'spot' ? input.futures : input.spot,
                state: 'forming',
            });
            if (made && made.score >= s.minScore)
                return made;
        }
    }
    return null;
}
/* ------------------------------------------------------- вспомогательное */
function freshPivot(pivots, i) {
    const lowAt = pivots.lowAt[i] ?? null;
    if (lowAt !== null && lowAt !== (pivots.lowAt[i - 1] ?? null))
        return { kind: 'low', at: lowAt };
    const highAt = pivots.highAt[i] ?? null;
    if (highAt !== null && highAt !== (pivots.highAt[i - 1] ?? null))
        return { kind: 'high', at: highAt };
    return null;
}
function pivotAt(bars, series, index, kind) {
    const bar = bars[index];
    const value = series?.[index];
    if (!bar || value === null || value === undefined)
        return null;
    return { at: bar.time, index, kind, price: kind === 'low' ? bar.low : bar.high, value };
}
/**
 * Разброс изменений ряда.
 *
 * Именно изменений, а не самого ряда: накопленный CVD растёт месяцами, и
 * его собственное отклонение говорит о длине истории, а не о том, крупное
 * ли движение потока мы видим.
 */
function changeDeviation(series, period) {
    const changes = series.map((value, i) => {
        const previous = series[i - 1];
        if (value === null || value === undefined || previous === null || previous === undefined)
            return 0;
        return value - previous;
    });
    return rollingStdDev(changes, period);
}
function average(values, period) {
    const out = new Array(values.length).fill(null);
    let sum = 0;
    for (let i = 0; i < values.length; i += 1) {
        sum += values[i];
        if (i >= period)
            sum -= values[i - period];
        if (i >= period - 1)
            out[i] = sum / period;
    }
    return out;
}
/** Изменение ряда между вершинами в процентах; `null` — данных нет. */
function changeBetween(series, from, to) {
    const start = series?.[from];
    const end = series?.[to];
    if (start === null || start === undefined || end === null || end === undefined)
        return null;
    if (start === 0)
        return null;
    return ((end - start) / Math.abs(start)) * 100;
}
/**
 * Чистота вершины: насколько она выступает над соседями.
 *
 * Вершина, едва заметная среди соседних баров, — это шум, даже если
 * формально подтверждена окном. Меряется в ATR, как и всё остальное.
 */
function pivotQuality(bars, from, to, s) {
    /** Насколько вершина выступает над ближайшим соседом того же рода. */
    const prominence = (pivot) => {
        const left = Math.max(0, pivot.index - s.left);
        const right = Math.min(bars.length - 1, pivot.index + s.right);
        let nearest = null;
        for (let i = left; i <= right; i += 1) {
            if (i === pivot.index)
                continue;
            const bar = bars[i];
            const value = pivot.kind === 'low' ? bar.low : bar.high;
            // Ближайший сосед: у впадины самый низкий из остальных, у вершины
            // самый высокий. Чем он дальше, тем чище экстремум.
            nearest =
                nearest === null ? value : pivot.kind === 'low' ? Math.min(nearest, value) : Math.max(nearest, value);
        }
        return nearest === null ? 0 : Math.abs(pivot.price - nearest);
    };
    const range = atr(bars.map((bar) => bar.high), bars.map((bar) => bar.low), bars.map((bar) => bar.close), s.atrPeriod)[to.index] ?? null;
    if (range === null || range <= 0)
        return 0;
    return ratio(Math.min(prominence(from), prominence(to)) / range, 1);
}
function positioningOf(side, kind, oiChange, source) {
    if (oiChange === null || source === 'flow')
        return oiChange === null ? null : 'flat';
    if (Math.abs(oiChange) < 0.5)
        return 'flat';
    // Сторону задаёт движение цены: у бычьего расхождения цена падала.
    const priceDown = kind === 'classic' ? side === 'bullish' : side === 'bearish';
    if (priceDown)
        return oiChange > 0 ? 'new-shorts' : 'long-exit';
    return oiChange > 0 ? 'new-longs' : 'short-covering';
}
const POSITIONING_WORDS = {
    'new-longs': 'Открытый интерес растёт: в движение заходят новые ставки на рост.',
    'new-shorts': 'Открытый интерес растёт: открываются новые ставки на падение.',
    'short-covering': 'Открытый интерес падает: рост идёт на закрытии ставок на падение, а не на новых покупках.',
    'long-exit': 'Открытый интерес падает: снижение идёт на закрытии ставок на рост, а не на новых продажах.',
    flat: 'Открытый интерес почти не изменился: новых денег движение не привлекло.',
};
const SOURCE_WORDS = {
    spot: 'покупки самих монет на споте',
    futures: 'ставки на цену в контрактах',
    flow: 'спот против контрактов',
};
function explain(source, kind, side, positioning, priceZ, valueZ, oiChange) {
    const strength = `Цена прошла ${priceZ.toFixed(1)} обычного размаха бара, поток разошёлся на ${valueZ.toFixed(1)} своего.`;
    const oi = positioning ? ` ${POSITIONING_WORDS[positioning]}` : '';
    if (source === 'flow') {
        const text = side === 'bullish'
            ? `Потоки спорят: монеты на споте покупают, а в контрактах в это же время продают. Спот принимает на себя продажи деривативов. ${strength}${oi}`
            : `Потоки спорят: на споте монеты продают, а в контрактах покупают. Движение держится на ставках, а не на покупке самих монет. ${strength}${oi}`;
        return {
            text,
            plain: side === 'bullish'
                ? 'Монеты скупают напрямую, пока в контрактах ставят на падение.'
                : 'В контрактах ставят на рост, пока сами монеты продают.',
        };
    }
    const what = SOURCE_WORDS[source];
    if (kind === 'classic') {
        const text = side === 'bullish'
            ? `Цена поставила новый минимум, а ${what} за ней не пошли: продавцы давят слабее, чем выглядит по цене. ${strength}${oi}`
            : `Цена поставила новый максимум, а ${what} за ней не пошли: покупатели выдыхаются. ${strength}${oi}`;
        return {
            text,
            plain: side === 'bullish'
                ? 'Цена ниже, а продают уже меньше.'
                : 'Цена выше, а покупают уже меньше.',
        };
    }
    const text = side === 'bullish'
        ? `Откат цены оказался мельче, чем просадка потока: ${what} слабеют сильнее самой цены. Так выглядит передышка внутри роста, а не разворот. ${strength}${oi}`
        : `Отскок цены оказался слабее, чем рост потока: цена не смогла обновить максимум. Так выглядит передышка внутри снижения. ${strength}${oi}`;
    return {
        text,
        plain: side === 'bullish'
            ? 'Похоже на передышку внутри роста, а не на разворот.'
            : 'Похоже на передышку внутри снижения.',
    };
}
function gradeOf(score) {
    if (score < 30)
        return 'weak';
    if (score < 50)
        return 'moderate';
    if (score < 70)
        return 'strong';
    if (score < 85)
        return 'very-strong';
    return 'extreme';
}
function part(key, label, share, weight) {
    return { key, label, share: round(Math.max(0, Math.min(1, share))), weight };
}
/** Доля от «полного» значения: больше полного — единица. */
function ratio(value, full) {
    if (full <= 0)
        return 0;
    return Math.max(0, Math.min(1, value / full));
}
function round(value) {
    return Math.round(value * 100) / 100;
}
//# sourceMappingURL=divergence-engine.js.map